/**
 * Camera store - Manages captured media state AND persistent camera stream.
 *
 * iOS PWA Workaround: iOS Safari re-prompts for camera permissions every time
 * getUserMedia is called. By keeping the stream alive at app level (never
 * releasing it during SPA navigation), we avoid repeated permission prompts.
 *
 * See: https://bugs.webkit.org/show_bug.cgi?id=215884
 *
 * THE VIEWFINDER STREAM IS VIDEO ONLY (BUG-022). It used to ask for audio too,
 * which made iOS open a recording audio session just to show a preview: a loud
 * system tone on launch, extra latency before the first frame, and - because
 * the stream above is deliberately never released - the microphone stayed live
 * on every screen in the app, orange indicator and all, while you read
 * scripture. The mic is acquired on demand when a recording starts (acquireMic).
 *
 * MIC LIFECYCLE (BUG-028/BUG-054): hold-threshold -> record -> PARK (45s) ->
 * release. The mic is requested only once a shutter press has held past the
 * 300ms threshold and is definitely a video — NEVER at press time, because iOS
 * pauses background music the moment any mic opens and never resumes it, so a
 * photo tap must not touch it (BUG-054). A cold mic makes the first clip pay
 * its grant at the threshold; after a recording the mic stays warm (parkMic)
 * so back-to-back clips start instantly. It's hard-released on photo, on
 * leaving the camera panel, on backgrounding, and when the park timer runs
 * out — so the BUG-022 promise (no mic on Chat/Faith, no mic while idle) holds.
 */
import { create } from 'zustand';
import type { CapturedMedia, DrawingStroke } from '../types';

// In-flight mic request, shared so concurrent callers never race two
// getUserMedia calls. `micWanted` closes the race where the mic is released
// (photo just past the hold threshold, panel left, app backgrounded) before a
// pending grant resolves — without it, a mic granted after releaseMic() would
// stay open forever.
let micRequest: Promise<MediaStream | null> | null = null;
let micWanted = false;

// Pending parked-mic release (BUG-028). acquireMic cancels it — a new capture
// claims the warm mic; releaseMic cancels it too — a hard release always wins.
let parkTimer: ReturnType<typeof setTimeout> | null = null;
const MIC_PARK_MS = 45_000;

function clearParkTimer() {
  if (parkTimer !== null) {
    clearTimeout(parkTimer);
    parkTimer = null;
  }
}

type CameraFacing = 'user' | 'environment';

// FEAT-012: the viewfinder runs at 4K so PHOTOS grab ~8MP frames — the honest
// iOS-PWA ceiling (no native photo pipeline). Recording drops the live track
// to 1080p at record start (setCaptureProfile) and restores 4K after; `ideal`
// constraints never over-constrain, older phones just give their max.
export const STREAM_RES = { width: 3840, height: 2160 } as const;
export const RECORD_RES = { width: 1920, height: 1080 } as const;

// FEAT-028 wave 4: the camera encodes the DELIVERY copy, at full resolution.
// Measured on four of Nation's own photos (2026-07-30): at q0.9 a 2160x3840
// frame lands at ~1.03 MB against the 2.31 MB JPEG the phone used to upload,
// and the server stops re-compressing photos entirely. PSNR 42.9 dB, well
// inside the range called visually indistinguishable — and what a recipient
// receives goes UP, from a 1440x2560 q85 rendition to every pixel at q90.
// FEAT-025 owns this number if the quality grid ever moves it.
export const PHOTO_DELIVERY_QUALITY = 0.9;
// **This is the iPhone's number, not a fallback in any meaningful sense.**
// WebKit has never implemented WebP encoding from a canvas — Safari displays
// WebP happily and cannot write it — so every iPhone lands here, and iPhones
// are most of who uses Momento.
//
// It is q85, not the q95 photos used before wave 4, because this file is now
// the DELIVERY copy rather than an archive master. Measured on Nation's own
// 2160x3840 photos (2026-07-31): q85 lands at 1.17 MB against the 2.31 MB the
// phone used to upload, at 41.7 dB — versus 1.03 MB and 42.9 dB for the WebP
// the device cannot make. 0.14 MB and 1.2 dB is what iOS costs us, and it is
// not worth 300KB of wasm on the shutter path to win back.
export const PHOTO_FALLBACK_QUALITY = 0.85;

// ⚠️ ASK LANDSCAPE-SHAPED. NEVER swap these to portrait (BUG-072).
// v2.21.0 tried asking for 2160x3840 in portrait, reasoning that a camera hands
// back the shape you ask for. iOS reads the requested aspect against the SENSOR
// and satisfies a 9:16 request by CROPPING it — the whole camera came up
// permanently zoomed in, 0.5x framed like 1x, and the selfie camera with it.
// The recording's shape is guaranteed by the pinned canvas in CameraSection
// (utils/recordingGeometry.ts), which needs nothing from these constraints.
const streamConstraints = (extra?: MediaTrackConstraints): MediaTrackConstraints => ({
  width: { ideal: STREAM_RES.width },
  height: { ideal: STREAM_RES.height },
  frameRate: { ideal: 30 },
  ...extra,
});

type CameraError = 'no-camera' | 'permission-denied' | 'generic' | null;

// Lens types for multi-camera phones
export type LensType = 'ultrawide' | 'wide' | 'telephoto';

export interface CameraLens {
  deviceId: string;
  label: string;
  type: LensType;
  zoomFactor: number; // Actual zoom value (0.5, 1, 2, 3, 5.5, etc.)
  zoomLabel: string;  // Display label ("0.5", "1", "3", "5.5")
}

// Text bar from draft
interface DraftTextBar {
  text: string;
  /** % of the PICTURE's height — MEDIA SPACE, see `TextBar` in ../types. */
  y: number;
}

// Reply context for quick replies from DM
interface ReplyContext {
  friendId: number;
  friendName: string;
}

// Multi-shot: how many shots one story batch may hold
export const MAX_SHOT_QUEUE = 8;

/**
 * How far along a camera-roll pick is (FEAT-032/034).
 *
 * It lives in the store rather than in CameraSection because it is shown in the
 * app's top pill rail with every other "something is happening" pill — Nation,
 * 2026-08-04: *"that should just be a clean pill at the top, like the rest of
 * the pills, so they can still send snaps or whatever else in the meantime."*
 * A dark box in the middle of the viewfinder read as a modal he had to wait on.
 */
export interface PickProgress {
  done: number;
  total: number;
  mode: 'add' | 'split' | 'ready';
  /** Percent of the current item's encode, when one is running. */
  pct?: number;
}

interface CameraStore {
  // Current capture being edited/sent
  currentCapture: CapturedMedia | null;

  /** Camera-roll pick in flight, or null. Rendered by GlobalToast's pill rail. */
  pickProgress: PickProgress | null;
  setPickProgress: (progress: PickProgress | null) => void;

  // Multi-shot queue: shots waiting to go out together as one story batch,
  // capped at MAX_SHOT_QUEUE.
  //
  // NOT baked — each entry keeps its ORIGINAL blob and carries its own
  // `overlays` as data, which the send passes through and the server renders.
  // (`mediaBaking.ts` burns pixels only for saving to the device.) This comment
  // used to claim the opposite and cost FEAT-035 a wrong plan; the distinction
  // is what makes re-editing a queued shot cheap rather than a rebuild.
  captureQueue: CapturedMedia[];

  // FEAT-017: identity of the live batch session. Minted when the queue goes
  // 0→1 and stamped on every draft row of the session (queued shots AND the
  // capture still in the editor), so a batch restores as ONE group even next
  // to leftover rows from an older session.
  batchId: string | null;

  // Current editing session overlays (persists during navigation)
  currentTextBars: DraftTextBar[];
  currentDrawingStrokes: DrawingStroke[];

  // Draft overlays (restored when resuming a draft)
  draftTextBars: DraftTextBar[];
  draftDrawingStrokes: DrawingStroke[];

  // Persistent camera stream (kept alive to avoid iOS permission re-prompts).
  // VIDEO ONLY - see the file header.
  stream: MediaStream | null;
  // Microphone, held only for the duration of a video recording
  micStream: MediaStream | null;
  facing: CameraFacing;
  error: CameraError;
  isInitializing: boolean;

  // Multi-lens support (back camera only)
  availableLenses: CameraLens[];
  selectedLens: CameraLens | null;
  lensesDetected: boolean;

  // Reply context for quick replies from DM
  replyContext: ReplyContext | null;

  // Actions
  /**
   * `keepUrl` hands this capture's object URL to someone else rather than
   * revoking it — the multi-shot queue, which since FEAT-028 wave 2 holds the
   * SAME bytes the editor had (nothing is baked, so no new blob is minted and
   * no new url with it). Without it, queueing a shot would revoke the url the
   * queue had just taken ownership of and every thumbnail would go blank.
   */
  setCurrentCapture: (media: CapturedMedia | null, opts?: { keepUrl?: boolean }) => void;
  /** Queue a shot for the batch (bytes + its own decoration). False when full. */
  addToQueue: (media: CapturedMedia) => boolean;
  /** Drop the whole queue, revoking its object URLs. */
  clearQueue: () => void;
  /** Drop one queued shot, revoking its object URL. Tap-driven only. */
  removeFromQueue: (index: number) => void;
  /** Take the LAST queued shot back out WITHOUT revoking its URL — ownership
      transfers to whoever sets it as currentCapture. Tap-driven only. */
  popFromQueue: () => CapturedMedia | null;
  /** FEAT-017: put a restored draft batch back as the live queue. Fresh object
      URLs are the caller's job; revokes nothing. */
  restoreQueue: (shots: CapturedMedia[], batchId: string) => void;
  setCurrentOverlays: (textBars: DraftTextBar[], drawingStrokes: DrawingStroke[]) => void;
  clearCurrentOverlays: () => void;
  setDraftOverlays: (textBars: DraftTextBar[], drawingStrokes: DrawingStroke[]) => void;
  clearDraftOverlays: () => void;

  // Stream management
  initStream: (force?: boolean) => Promise<MediaStream | null>;
  switchCamera: () => Promise<void>;
  setCaptureProfile: (profile: 'photo' | 'record') => Promise<void>;
  pauseStream: () => void;      // Disables tracks but keeps stream alive (no re-prompt)
  resumeStream: () => void;     // Re-enables tracks
  destroyStream: () => void;    // Actually stops tracks (use only on logout)

  // Microphone - recording only, never for the viewfinder
  acquireMic: () => Promise<MediaStream | null>;
  releaseMic: () => void;
  parkMic: () => void;     // Keep the mic warm 45s after a recording (BUG-028)

  // Lens management (back camera only)
  detectLenses: () => Promise<void>;
  selectLens: (lens: CameraLens) => Promise<void>;

  // Reply context management
  setReplyContext: (context: ReplyContext | null) => void;
  clearReplyContext: () => void;
}

export const useCameraStore = create<CameraStore>((set, get) => ({
  currentCapture: null,
  pickProgress: null,
  setPickProgress: (pickProgress) => set({ pickProgress }),
  captureQueue: [],
  batchId: null,
  currentTextBars: [],
  currentDrawingStrokes: [],
  draftTextBars: [],
  draftDrawingStrokes: [],
  stream: null,
  micStream: null,
  facing: 'user', // Default to front-facing (selfie) camera
  error: null,
  isInitializing: false,
  availableLenses: [],
  selectedLens: null,
  lensesDetected: false,
  replyContext: null,

  setCurrentCapture: (media, opts) => {
    // Clean up old URL when clearing or replacing capture
    const { currentCapture } = get();
    if (
      currentCapture?.url &&
      !opts?.keepUrl &&
      (!media || media.url !== currentCapture.url)
    ) {
      URL.revokeObjectURL(currentCapture.url);
    }
    set({ currentCapture: media });
  },

  addToQueue: (media) => {
    const { captureQueue, batchId } = get();
    if (captureQueue.length >= MAX_SHOT_QUEUE) return false;
    set({
      captureQueue: [...captureQueue, media],
      batchId: batchId ?? crypto.randomUUID(),
    });
    return true;
  },

  clearQueue: () => {
    for (const shot of get().captureQueue) {
      if (shot.url) URL.revokeObjectURL(shot.url);
    }
    set({ captureQueue: [], batchId: null });
  },

  // BUG-075: an empty queue has no batch. These two used to leave batchId set,
  // so every capture taken afterwards was stamped with a dead batch id — and
  // the drafts sheet groups by that id, collapsing independent moments into one
  // tile and hiding all but the last.
  removeFromQueue: (index) => {
    const { captureQueue } = get();
    const shot = captureQueue[index];
    if (!shot) return;
    if (shot.url) URL.revokeObjectURL(shot.url);
    const remaining = captureQueue.filter((_, i) => i !== index);
    set({ captureQueue: remaining, batchId: remaining.length > 0 ? get().batchId : null });
  },

  popFromQueue: () => {
    const { captureQueue } = get();
    const shot = captureQueue[captureQueue.length - 1];
    if (!shot) return null;
    const remaining = captureQueue.slice(0, -1);
    set({ captureQueue: remaining, batchId: remaining.length > 0 ? get().batchId : null });
    return shot;
  },

  restoreQueue: (shots, batchId) => set({ captureQueue: shots, batchId }),

  setCurrentOverlays: (textBars, drawingStrokes) => set({
    currentTextBars: textBars,
    currentDrawingStrokes: drawingStrokes,
  }),

  clearCurrentOverlays: () => set({
    currentTextBars: [],
    currentDrawingStrokes: [],
  }),

  setDraftOverlays: (textBars, drawingStrokes) => set({
    draftTextBars: textBars,
    draftDrawingStrokes: drawingStrokes,
  }),

  clearDraftOverlays: () => set({
    draftTextBars: [],
    draftDrawingStrokes: [],
  }),

  /**
   * Initialize camera stream. Returns existing stream if already active.
   * Only calls getUserMedia if no stream exists (avoids iOS permission re-prompts).
   * Includes video stabilization when available.
   */
  initStream: async (force = false) => {
    const { stream, facing, isInitializing, selectedLens } = get();

    // Already have an active stream - reuse it (unless a rebuild is forced,
    // e.g. after iOS suspended the PWA and left the tracks muted/dead)
    if (!force && stream && stream.active) {
      return stream;
    }

    // Already initializing - wait
    if (isInitializing) {
      return null;
    }

    // Release the old stream first — iOS won't hand out the camera again
    // while dead tracks still hold it
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      set({ stream: null });
    }

    set({ isInitializing: true, error: null });

    try {
      const videoConstraints = streamConstraints();

      // Use specific device if we have a selected lens (back camera)
      if (facing === 'environment' && selectedLens) {
        videoConstraints.deviceId = { exact: selectedLens.deviceId };
      } else {
        videoConstraints.facingMode = facing;
      }

      // Video only — audio is acquired at record time (see file header)
      const newStream = await navigator.mediaDevices.getUserMedia({
        video: videoConstraints,
      });

      // Try to enable video stabilization if supported
      const videoTrack = newStream.getVideoTracks()[0];
      if (videoTrack) {
        try {
          const capabilities = videoTrack.getCapabilities() as MediaTrackCapabilities & {
            videoStabilizationMode?: string[];
          };
          if (capabilities.videoStabilizationMode?.includes('cinematic')) {
            await videoTrack.applyConstraints({
              // @ts-expect-error - videoStabilizationMode not in standard types yet
              videoStabilizationMode: 'cinematic'
            });
          } else if (capabilities.videoStabilizationMode?.includes('standard')) {
            await videoTrack.applyConstraints({
              // @ts-expect-error - videoStabilizationMode not in standard types yet
              videoStabilizationMode: 'standard'
            });
          }
        } catch {
          // Stabilization not supported, continue without it
        }
      }

      set({ stream: newStream, isInitializing: false });
      return newStream;
    } catch (err) {
      const error = err as Error;
      let errorType: CameraError = 'generic';

      if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        errorType = 'no-camera';
      } else if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        errorType = 'permission-denied';
      }

      set({ error: errorType, isInitializing: false });
      return null;
    }
  },

  /**
   * Switch between front and back camera.
   * This WILL request permissions again (unavoidable when changing camera),
   * but normal navigation won't trigger re-prompts.
   */
  switchCamera: async () => {
    const { stream, facing } = get();

    // Stop existing stream tracks
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }

    const newFacing = facing === 'user' ? 'environment' : 'user';
    set({ facing: newFacing, stream: null });

    try {
      const newStream = await navigator.mediaDevices.getUserMedia({
        video: streamConstraints({ facingMode: newFacing }),
      });
      set({ stream: newStream, error: null });
    } catch (err) {
      const error = err as Error;
      let errorType: CameraError = 'generic';

      if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        errorType = 'no-camera';
      } else if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        errorType = 'permission-denied';
      }

      set({ error: errorType });
    }
  },

  /**
   * Pause the camera stream by disabling tracks.
   * Stream stays alive = no permission re-prompt when resuming.
   * Camera LED turns off, but permission persists.
   * USE THIS for normal navigation away from camera.
   */
  /**
   * Swap the live track's resolution without touching getUserMedia (no iOS
   * permission re-prompt): 'record' drops to 1080p so MediaRecorder never
   * sees a 4K frame; 'photo' restores 4K for the next shutter tap. Failure
   * is swallowed — a wedged swap just records bigger files, and the existing
   * initStream(force) recovery fences a wedged track.
   */
  setCaptureProfile: async (profile) => {
    const track = get().stream?.getVideoTracks()[0];
    if (!track) return;
    const res = profile === 'record' ? RECORD_RES : STREAM_RES;
    try {
      await track.applyConstraints({
        width: { ideal: res.width },
        height: { ideal: res.height },
      });
      const { width, height } = track.getSettings();
      console.log(`[camera] profile=${profile} track=${width}x${height}`);
    } catch {
      // never let a resolution swap kill the stream
    }
  },

  pauseStream: () => {
    const { stream } = get();
    if (stream) {
      stream.getTracks().forEach(track => {
        track.enabled = false;
      });
      console.log('[CameraStore] Stream paused (tracks disabled, stream alive)');
    }
  },

  /**
   * Resume the camera stream by re-enabling tracks.
   * Instant camera restart, no permission prompt needed.
   */
  resumeStream: () => {
    const { stream } = get();
    if (stream && stream.active) {
      stream.getTracks().forEach(track => {
        track.enabled = true;
      });
      console.log('[CameraStore] Stream resumed (tracks enabled)');
    }
  },

  /**
   * Destroy the camera stream completely.
   * ONLY call this on logout or when truly closing the app.
   * This WILL require a new permission prompt next time.
   */
  destroyStream: () => {
    const { stream } = get();
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      set({ stream: null });
      console.log('[CameraStore] Stream destroyed (tracks stopped)');
    }
    get().releaseMic();
  },

  /**
   * Get the microphone for a recording. Called when the hold threshold fires
   * and a recording actually starts — photos never touch the mic, so they
   * never stop the user's music (BUG-054). A cold mic pays its grant here;
   * a parked one (BUG-028) is reclaimed instantly. Concurrent callers share
   * one request.
   *
   * Resolves to null if the mic is unavailable or denied — a silent video is
   * better than a failed one.
   */
  acquireMic: async () => {
    clearParkTimer(); // a new capture claims the parked mic (BUG-028)
    micWanted = true;

    const { micStream } = get();
    if (micStream && micStream.active) return micStream;
    if (micRequest) return micRequest;

    micRequest = navigator.mediaDevices
      // The RECORDING mic, not the call mic (BUG-102). Asked with no
      // constraints, the browser hands back its voice-call chain — echo
      // cancellation, noise suppression and auto gain all ON — which band-
      // limits the signal and gates out music and ambience. A camcorder has no
      // far end to echo-cancel, so the speech processing comes off and the clip
      // records what the room actually sounded like. WHEN the mic is acquired
      // is BUG-054's rule and is not this constraint's business.
      .getUserMedia({
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
        },
      })
      .then((mic) => {
        // Released while we were waiting (quick tap = photo, not video)
        if (!micWanted) {
          mic.getTracks().forEach(track => track.stop());
          return null;
        }
        set({ micStream: mic });
        return mic;
      })
      .catch(() => null)
      .finally(() => {
        micRequest = null;
      });

    return micRequest;
  },

  /**
   * Stop the microphone. Called when a recording ends, and when a press that
   * crossed the hold threshold turns out to be a photo — the mic must never
   * outlive the capture that needed it. Safe to call when no mic is held.
   */
  releaseMic: () => {
    clearParkTimer();
    micWanted = false;
    const { micStream } = get();
    if (micStream) {
      micStream.getTracks().forEach(track => track.stop());
      set({ micStream: null });
      console.log('[CameraStore] Mic released');
    }
  },

  /**
   * Keep the mic warm after a recording instead of killing it (BUG-028), so
   * back-to-back clips skip the ~1s getUserMedia that made recording start
   * late. Releases on its own after MIC_PARK_MS; any hard releaseMic (photo,
   * leaving the camera panel, backgrounding) still wins immediately.
   */
  parkMic: () => {
    const { micStream } = get();
    if (!micStream || !micStream.active) return;
    clearParkTimer();
    parkTimer = setTimeout(() => {
      parkTimer = null;
      get().releaseMic();
    }, MIC_PARK_MS);
  },

  /**
   * Detect available lenses on the back camera.
   * Fast label-based detection - no probing, instant results.
   */
  detectLenses: async () => {
    const { lensesDetected } = get();
    if (lensesDetected) return;

    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(d => d.kind === 'videoinput');

      // Find back cameras by label
      const backCameras = videoDevices.filter(d => {
        const label = d.label.toLowerCase();
        // Exclude front cameras
        if (label.includes('front') || label.includes('facetime') || label.includes('selfie')) {
          return false;
        }
        // Include back cameras
        return label.includes('back') || label.includes('rear') ||
               label.includes('wide') || label.includes('ultra') || label.includes('tele') ||
               label.includes('environment') || label.includes('0') || label.includes('camera');
      });

      if (backCameras.length <= 1) {
        set({ lensesDetected: true, availableLenses: [] });
        return;
      }

      // Classify each camera by label
      const lenses: CameraLens[] = [];

      for (const device of backCameras) {
        const label = device.label.toLowerCase();

        // Detect ultrawide (0.5x)
        if (label.includes('ultra') || label.includes('0.5') || label.includes('.5')) {
          lenses.push({
            deviceId: device.deviceId,
            label: device.label,
            type: 'ultrawide',
            zoomFactor: 0.5,
            zoomLabel: '.5',
          });
        }
        // Detect telephoto (2x, 3x, 5x, etc.)
        else if (label.includes('tele') || label.includes('2x') || label.includes('3x') ||
                 label.includes('5x') || label.includes('zoom')) {
          const match = label.match(/(\d+(?:\.\d+)?)\s*x/i);
          const zoom = match ? parseFloat(match[1]) : 2;
          lenses.push({
            deviceId: device.deviceId,
            label: device.label,
            type: 'telephoto',
            zoomFactor: zoom,
            zoomLabel: zoom.toString(),
          });
        }
        // Default: main wide camera (1x)
        else if (label.includes('wide') || label.includes('back') || label.includes('rear') ||
                 label.includes('main') || label.includes('camera 0') || label.includes('environment')) {
          // Only add if we don't already have a wide lens
          if (!lenses.some(l => l.type === 'wide')) {
            lenses.push({
              deviceId: device.deviceId,
              label: device.label,
              type: 'wide',
              zoomFactor: 1,
              zoomLabel: '1',
            });
          }
        }
      }

      // Sort: ultrawide first, then wide, then telephoto
      lenses.sort((a, b) => a.zoomFactor - b.zoomFactor);

      // Default to 1x (wide) lens
      const defaultLens = lenses.find(l => l.type === 'wide') || lenses[0];

      set({
        lensesDetected: true,
        availableLenses: lenses,
        selectedLens: defaultLens || null,
      });
    } catch (err) {
      console.error('Failed to detect lenses:', err);
      set({ lensesDetected: true, availableLenses: [] });
    }
  },

  /**
   * Switch to a specific lens (back camera only).
   * Includes video stabilization when available.
   */
  selectLens: async (lens: CameraLens) => {
    const { stream, facing, selectedLens } = get();

    // Only works for back camera
    if (facing === 'user') return;

    // Already on this lens
    if (selectedLens?.deviceId === lens.deviceId) return;

    // Stop existing stream
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }

    set({ selectedLens: lens, stream: null });

    try {
      const newStream = await navigator.mediaDevices.getUserMedia({
        video: streamConstraints({ deviceId: { exact: lens.deviceId } }),
      });

      // Try to enable stabilization
      const videoTrack = newStream.getVideoTracks()[0];
      if (videoTrack) {
        try {
          const capabilities = videoTrack.getCapabilities() as MediaTrackCapabilities & {
            videoStabilizationMode?: string[];
          };
          if (capabilities.videoStabilizationMode?.includes('cinematic')) {
            await videoTrack.applyConstraints({
              // @ts-expect-error - videoStabilizationMode not in standard types
              videoStabilizationMode: 'cinematic'
            });
          } else if (capabilities.videoStabilizationMode?.includes('standard')) {
            await videoTrack.applyConstraints({
              // @ts-expect-error - videoStabilizationMode not in standard types
              videoStabilizationMode: 'standard'
            });
          }
        } catch {
          // Stabilization not supported
        }
      }

      set({ stream: newStream, error: null });
    } catch (err) {
      console.error('Failed to switch lens:', err);
      // Fall back to generic back camera
      try {
        const fallbackStream = await navigator.mediaDevices.getUserMedia({
          video: streamConstraints({ facingMode: 'environment' }),
        });
        set({ stream: fallbackStream, error: null });
      } catch {
        set({ error: 'generic' });
      }
    }
  },

  /**
   * Set reply context for quick replies from DM.
   * When set, camera will auto-select this friend as recipient after capture.
   */
  setReplyContext: (context) => set({ replyContext: context }),

  /**
   * Clear reply context after sending or canceling.
   */
  clearReplyContext: () => set({ replyContext: null }),
}));
